import * as preact from "preact";

import readlangClient, { requestErrorMessage } from "readlangClient";

type Props = {
  appRouter: any;
};

type Bucket = {
  bucketStart: string;
  count: number;
  p50: number;
  p90: number;
  p95: number;
  p99: number;
};

type BasePathOption = {
  basePath: string;
  count: number;
};

type State = {
  buckets?: Bucket[];
  basePaths?: BasePathOption[];
  basePath: string;
  sinceHours: number;
  loading: boolean;
  error?: string;
  hoverIndex?: number;
};

const SINCE_HOURS_OPTIONS = [1, 6, 24, 72, 168];

const SERIES = [
  { key: "p50" as const, label: "p50", color: "#1f77b4" },
  { key: "p90" as const, label: "p90", color: "#2ca02c" },
  { key: "p95" as const, label: "p95", color: "#ff7f0e" },
  { key: "p99" as const, label: "p99", color: "#d62728" },
];

const CHART_WIDTH = 900;
const CHART_HEIGHT = 360;
const PADDING_LEFT = 60;
const PADDING_RIGHT = 20;
const PADDING_TOP = 20;
const PADDING_BOTTOM = 50;

export default class AdminAPILogsView extends preact.Component<Props, State> {
  private mounted = false;
  private statsRequestVersion = 0;
  private basePathsRequestVersion = 0;

  constructor(props: Props) {
    super(props);
    this.state = {
      basePath: "",
      sinceHours: 24,
      loading: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.fetchBasePaths();
    this.fetchStats();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  private isCurrentStatsRequest(requestVersion: number) {
    return this.mounted && this.statsRequestVersion === requestVersion;
  }

  private isCurrentBasePathsRequest(requestVersion: number) {
    return this.mounted && this.basePathsRequestVersion === requestVersion;
  }

  async fetchBasePaths() {
    const requestVersion = ++this.basePathsRequestVersion;
    try {
      const data = (
        await readlangClient.request("GET", "/api/admin/apiLogs/basePaths", {
          sinceHours: this.state.sinceHours,
        })
      ).data as { basePaths: BasePathOption[] };
      if (!this.isCurrentBasePathsRequest(requestVersion)) return;
      this.setState({ basePaths: data.basePaths });
    } catch (err) {
      if (!this.isCurrentBasePathsRequest(requestVersion)) return;
      this.setState({ error: requestErrorMessage(err) });
    }
  }

  async fetchStats() {
    const requestVersion = ++this.statsRequestVersion;
    this.setState({ loading: true, error: undefined });
    try {
      const params: Record<string, string | number> = {
        sinceHours: this.state.sinceHours,
      };
      if (this.state.basePath) {
        params.basePath = this.state.basePath;
      }
      const data = (
        await readlangClient.request(
          "GET",
          "/api/admin/apiLogs/latencyStats",
          params
        )
      ).data as { buckets: Bucket[] };
      if (!this.isCurrentStatsRequest(requestVersion)) return;
      this.setState({
        buckets: data.buckets,
        loading: false,
        hoverIndex: undefined,
      });
    } catch (err) {
      if (!this.isCurrentStatsRequest(requestVersion)) return;
      this.setState({ error: requestErrorMessage(err), loading: false });
    }
  }

  onSinceHoursChange = (sinceHours: number) => {
    this.setState({ sinceHours }, () => {
      this.fetchBasePaths();
      this.fetchStats();
    });
  };

  onBasePathChange = (basePath: string) => {
    this.setState({ basePath }, () => this.fetchStats());
  };

  renderChart() {
    const buckets = this.state.buckets;
    if (!buckets || buckets.length === 0) {
      return <p>No data in the selected window.</p>;
    }

    const plotWidth = CHART_WIDTH - PADDING_LEFT - PADDING_RIGHT;
    const plotHeight = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

    let maxLatency = 0;
    for (const bucket of buckets) {
      if (bucket.p99 > maxLatency) maxLatency = bucket.p99;
    }
    if (maxLatency === 0) maxLatency = 1;
    const yMax = Math.ceil(maxLatency / 100) * 100 || maxLatency;

    const firstTime = new Date(buckets[0].bucketStart).getTime();
    const lastTime = new Date(
      buckets[buckets.length - 1].bucketStart
    ).getTime();
    const timeSpan = Math.max(1, lastTime - firstTime);

    const xFor = (index: number) => {
      if (buckets.length === 1) return PADDING_LEFT + plotWidth / 2;
      const time = new Date(buckets[index].bucketStart).getTime();
      return PADDING_LEFT + ((time - firstTime) / timeSpan) * plotWidth;
    };
    const yFor = (latency: number) =>
      PADDING_TOP + plotHeight - (latency / yMax) * plotHeight;

    const yTickCount = 5;
    const yTicks: number[] = [];
    for (let index = 0; index <= yTickCount; index++) {
      yTicks.push((yMax * index) / yTickCount);
    }

    const xTickCount = Math.min(6, buckets.length);
    const xTickIndices: number[] = [];
    for (let index = 0; index < xTickCount; index++) {
      const bucketIndex = Math.round(
        (index * (buckets.length - 1)) / Math.max(1, xTickCount - 1)
      );
      if (!xTickIndices.includes(bucketIndex)) {
        xTickIndices.push(bucketIndex);
      }
    }

    const hoverIndex = this.state.hoverIndex;
    const hoverBucket =
      hoverIndex !== undefined && buckets[hoverIndex]
        ? buckets[hoverIndex]
        : undefined;

    return (
      <div class="latencyChart">
        <svg
          width={CHART_WIDTH}
          height={CHART_HEIGHT}
          onMouseLeave={() => this.setState({ hoverIndex: undefined })}
          onMouseMove={(event) => {
            const svg = event.currentTarget;
            const rect = svg.getBoundingClientRect();
            const xInSvg =
              ((event.clientX - rect.left) * CHART_WIDTH) / rect.width;
            const xRatio = (xInSvg - PADDING_LEFT) / Math.max(1, plotWidth);
            const index = Math.max(
              0,
              Math.min(
                buckets.length - 1,
                Math.round(xRatio * (buckets.length - 1))
              )
            );
            if (index !== this.state.hoverIndex) {
              this.setState({ hoverIndex: index });
            }
          }}
        >
          {yTicks.map((tick) => (
            <g>
              <line
                x1={PADDING_LEFT}
                x2={CHART_WIDTH - PADDING_RIGHT}
                y1={yFor(tick)}
                y2={yFor(tick)}
                stroke="#eee"
              />
              <text
                x={PADDING_LEFT - 8}
                y={yFor(tick) + 4}
                text-anchor="end"
                font-size="11"
                fill="#666"
              >
                {Math.round(tick)} ms
              </text>
            </g>
          ))}

          {xTickIndices.map((bucketIndex) => {
            const bucket = buckets[bucketIndex];
            const date = new Date(bucket.bucketStart);
            const label = `${String(date.getHours()).padStart(2, "0")}:${String(
              date.getMinutes()
            ).padStart(2, "0")}`;
            const dateLabel = `${date.getMonth() + 1}/${date.getDate()}`;
            return (
              <g>
                <line
                  x1={xFor(bucketIndex)}
                  x2={xFor(bucketIndex)}
                  y1={PADDING_TOP}
                  y2={CHART_HEIGHT - PADDING_BOTTOM}
                  stroke="#f5f5f5"
                />
                <text
                  x={xFor(bucketIndex)}
                  y={CHART_HEIGHT - PADDING_BOTTOM + 16}
                  text-anchor="middle"
                  font-size="11"
                  fill="#666"
                >
                  {label}
                </text>
                <text
                  x={xFor(bucketIndex)}
                  y={CHART_HEIGHT - PADDING_BOTTOM + 30}
                  text-anchor="middle"
                  font-size="10"
                  fill="#999"
                >
                  {dateLabel}
                </text>
              </g>
            );
          })}

          {SERIES.map((series) => {
            const points = buckets
              .map(
                (bucket, index) => `${xFor(index)},${yFor(bucket[series.key])}`
              )
              .join(" ");
            return (
              <polyline
                points={points}
                fill="none"
                stroke={series.color}
                stroke-width="2"
              />
            );
          })}

          {hoverBucket && hoverIndex !== undefined && (
            <line
              x1={xFor(hoverIndex)}
              x2={xFor(hoverIndex)}
              y1={PADDING_TOP}
              y2={CHART_HEIGHT - PADDING_BOTTOM}
              stroke="#999"
              stroke-dasharray="4 4"
            />
          )}

          {hoverBucket &&
            hoverIndex !== undefined &&
            SERIES.map((series) => (
              <circle
                cx={xFor(hoverIndex)}
                cy={yFor(hoverBucket[series.key])}
                r="3"
                fill={series.color}
              />
            ))}
        </svg>

        <div class="chartLegend">
          {SERIES.map((series) => (
            <span class="legendItem">
              <span
                class="legendSwatch"
                style={{ backgroundColor: series.color }}
              />
              {series.label}
            </span>
          ))}
        </div>

        {hoverBucket && (
          <div class="chartHoverDetail">
            <strong>
              {new Date(hoverBucket.bucketStart).toLocaleString()}
            </strong>
            <span> · {hoverBucket.count} req</span>
            {SERIES.map((series) => (
              <span class="hoverValue">
                <span
                  class="legendSwatch"
                  style={{ backgroundColor: series.color }}
                />
                {series.label}: {Math.round(hoverBucket[series.key])} ms
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  render() {
    const totalRequests = (this.state.buckets ?? []).reduce(
      (sum, bucket) => sum + bucket.count,
      0
    );

    return (
      <div class="adminAPILogsPage">
        <h1>
          <a href="/admin">Admin</a> - API Logs
        </h1>

        <div class="controls">
          <label>
            Window:{" "}
            <select
              value={String(this.state.sinceHours)}
              onChange={(event) => {
                this.onSinceHoursChange(
                  parseInt(event.currentTarget.value, 10)
                );
              }}
            >
              {SINCE_HOURS_OPTIONS.map((hours) => (
                <option value={String(hours)}>
                  {hours < 24 ? `${hours}h` : `${hours / 24}d`}
                </option>
              ))}
            </select>
          </label>

          <label style={{ marginLeft: 20 }}>
            basePath:{" "}
            <select
              value={this.state.basePath}
              onChange={(event) => {
                this.onBasePathChange(event.currentTarget.value);
              }}
            >
              <option value="">All ({totalRequests} req)</option>
              {(this.state.basePaths ?? []).map((option) => (
                <option value={option.basePath}>
                  {option.basePath} ({option.count})
                </option>
              ))}
            </select>
          </label>
        </div>

        {this.state.error && <p class="error">{this.state.error}</p>}

        {this.state.loading ? <p>Loading...</p> : this.renderChart()}
      </div>
    );
  }
}
